import { aHora24, completarHora, desdeHora24, diaNegocio, enmascararHora, faltaPeriodo, fueraDeAtencion, sumarDias, textoAtencion, type Periodo } from '@barbones/shared';
import { useEffect, useState } from 'react';
import { Campo, Input, Select } from '@/components/ui/form';
import { diaLargo } from '@/lib/utils';
import { useConfig } from './datos';
import { useHorarioAtencion } from './horarioAtencion';

interface Props {
  barberoId?: string | null;
  dia: string;
  onDia: (dia: string) => void;
  /** Hora local `HH:mm` (24 h) elegida; `''` mientras no haya una válida. */
  hora: string;
  onHora: (hora: string) => void;
  etiquetaFecha?: string;
  etiquetaHora?: string;
  /** Hora que ya tenía la cita (mismo día): si no cambia, no se avisa que cae fuera de horario. */
  horaActual?: string;
}

const MENSAJE_FORMATO = 'Escribe la hora como 09:10';

/**
 * Fecha (limitada al horizonte de agenda) y hora escrita `hh:mm` + AM/PM. Cualquier minuto vale;
 * el horario y los choques los valida la API al guardar.
 */
export function SelectorHorario({ barberoId, dia, onDia, hora, onHora, etiquetaFecha = 'Fecha', etiquetaHora = 'Hora', horaActual }: Props) {
  const horizonte = useConfig<number>('agenda.horizonteDias', 60);
  const hoy = diaNegocio(new Date());
  const atencion = useHorarioAtencion(barberoId ?? null, dia);
  const [texto, setTexto] = useState(hora ? desdeHora24(hora).texto : '');
  const [periodo, setPeriodo] = useState<Periodo | ''>(hora ? desdeHora24(hora).periodo : '');
  const [error, setError] = useState<string | undefined>();

  // La hora puede llegar de fuera (clic en la agenda, «Siguiente horario disponible», Antes/Después).
  useEffect(() => {
    if (!hora || aHora24(texto, periodo) === hora) return;
    const p = desdeHora24(hora);
    setTexto(p.texto);
    setPeriodo(p.periodo);
    setError(undefined);
    // Solo reacciona a cambios de `hora`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hora]);

  const cambiar = (nuevoTexto: string, nuevoPeriodo: Periodo | '') => {
    // 13–23 ya son 24 h: el periodo pasa a PM.
    const h = Number(nuevoTexto.split(':')[0]);
    const periodoFinal = /^\d{2}:\d{2}$/.test(nuevoTexto) && h >= 13 && h <= 23 ? 'PM' : nuevoPeriodo;
    setTexto(nuevoTexto);
    setPeriodo(periodoFinal);
    const valida = aHora24(nuevoTexto, periodoFinal);
    if (valida) setError(undefined);
    if ((valida ?? '') !== hora) onHora(valida ?? '');
  };

  return (
    <div className="grid gap-3">
      <Campo etiqueta={etiquetaFecha} requerido ayuda={`Se puede agendar hasta el ${diaLargo(sumarDias(hoy, horizonte))}.`}>
        {(p) => <Input {...p} type="date" value={dia} min={hoy} max={sumarDias(hoy, horizonte)} onChange={(e) => onDia(e.target.value)} />}
      </Campo>
      <div className="grid grid-cols-2 gap-2">
        <Campo etiqueta={etiquetaHora} requerido error={error}>
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              autoComplete="off"
              maxLength={5}
              placeholder="09:10"
              className="cifras"
              value={texto}
              onChange={(e) => cambiar(enmascararHora(e.target.value), periodo)}
              onBlur={() => {
                const t = completarHora(texto);
                if (!t) return setError(undefined);
                if (aHora24(t, periodo || 'AM') === null) {
                  setTexto(t);
                  return setError(MENSAJE_FORMATO);
                }
                const valida = aHora24(t, periodo);
                cambiar(valida ? desdeHora24(valida).texto : t, valida ? desdeHora24(valida).periodo : periodo);
              }}
            />
          )}
        </Campo>
        <Campo etiqueta="AM / PM" requerido ayuda={faltaPeriodo(texto, periodo) ? 'Elige AM o PM' : undefined}>
          {(p) => (
            <Select {...p} value={periodo} onChange={(e) => cambiar(texto, e.target.value as Periodo | '')}>
              <option value="">AM / PM</option>
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </Select>
          )}
        </Campo>
      </div>
      {atencion === 'descanso' ? (
        <p className="rounded-md bg-muted px-3 py-2 text-[13px] text-foreground-muted">Sin horarios ese día (o el barbero no tiene horario registrado).</p>
      ) : atencion && hora !== horaActual && fueraDeAtencion(hora, atencion) ? (
        // La API lo rechazaría al guardar; se avisa antes (p. ej. al pasar sola a «Sin asignar»).
        <p role="alert" className="cifras rounded-md bg-warning-soft px-3 py-2 text-[13px] text-warning">
          Fuera de horario. {textoAtencion(atencion)}
        </p>
      ) : atencion ? (
        <p className="cifras text-[12.5px] text-foreground-muted">{textoAtencion(atencion)}</p>
      ) : null}
    </div>
  );
}
